import CKEditor5 from 'components/CkEditor5';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';
import { createDropdownData } from 'utils/validators/HelperFunctions';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { getSaBookingFieldLabel } from '../saBookingFieldLabels';
import type { SaBookingDetailPayload } from '../SuperAdminBookingGuestDetailsSection';

/** Field order (API camelCase); rendered in this section only — excluded from generic booking field list. */
export const BOOKING_DETAILS_SECTION_FIELDS = [
  'bookingDate',
  'startDate',
  'endDate',
  'eventTypeId',
  'bookingConfirmationBy',
  'bookingStatus',
  'holdDays',
  'referredBy',
  'requestedChanges',
  'cancelReason',
  'additionalComments',
  'customerBooking',
  'isGuestBooking',
  'isCorporateBooking',
  'isComplementary',
  'externalBooking',
] as const;

const WIDE_FIELDS = new Set(['requestedChanges', 'cancelReason', 'additionalComments']);

type DropdownOpt = { value: number; label: string };
type StringDropdownOpt = { value: string; label: string };

/** Booking statuses BBR admin may pick (matches super-admin API validation). */
const SA_BOOKING_DETAIL_STATUS_OPTIONS: StringDropdownOpt[] = [
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'TENTATIVE', label: 'Tentative' },
  { value: 'WAITLISTED', label: 'Waitlisted' },
  { value: 'ENQUIRY', label: 'Enquiry' },
  { value: 'AWAITING_CONFIRMATION', label: 'Awaiting Confirmation' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'EXPIRED', label: 'Expired' },
];

function toId(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}
  const preventNumberInputScroll = (e: React.WheelEvent<HTMLDivElement>) => {
    const target = e.target as HTMLInputElement;
    target.blur();
  };
function coerceBooleanDisplay(raw: unknown): boolean {
  if (raw === true) return true;
  if (raw === false) return false;
  if (raw === null || raw === undefined) return false;
  if (typeof raw === 'string') {
    const s = raw.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'no' || s === '') return false;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw !== 0;
  }
  return Boolean(raw);
}

function rawToDateInput(raw: unknown): string {
  if (raw == null) return '';
  if (typeof raw === 'string') {
    const t = raw.trim();
    const m = t.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  if (Array.isArray(raw) && raw.length >= 3) {
    const y = Number(raw[0]);
    const mo = Number(raw[1]);
    const d = Number(raw[2]);
    if ([y, mo, d].every(n => Number.isFinite(n))) {
      return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const ms = raw > 1e12 ? raw : raw * 1000;
    const dt = new Date(ms);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toISOString().slice(0, 10);
    }
  }
  return '';
}

function strEnum(raw: unknown): string {
  if (raw == null) return '';
  return String(raw).trim();
}

function mergeStringOption(opts: StringDropdownOpt[], current: string): StringDropdownOpt[] {
  const c = current.trim();
  if (!c) return opts;
  if (opts.some(o => o.value === c)) return opts;
  return [{ value: c, label: c }, ...opts];
}

function statusAllowsCancelReasonEdit(status: string): boolean {
  return status === 'CANCELLED' || status === 'EXPIRED';
}

type BookingDetailsForm = {
  startDate: string;
  endDate: string;
  eventTypeId: number | null;
  bookingConfirmationBy: string;
  bookingStatus: string;
  holdDays: number;
  referredBy: string;
  cancelReason: string;
  additionalComments: string;
  customerBooking: boolean;
  isGuestBooking: boolean;
  isCorporateBooking: boolean;
  isComplementary: boolean;
  externalBooking: boolean;
  salesAccountId: number | null;
  salesBranchId: number | null;
};

const ReadOnlyBookingField: FC<{ label: string; value: string }> = ({ label, value }) => {
  const display = value || '—';
  const multiline = display.length > 72 || display.includes('\n');
  return (
    <TextField
      label={label}
      value={display}
      fullWidth
      variant="outlined"
      size="small"
      multiline={multiline}
      minRows={multiline ? 2 : 1}
      maxRows={multiline ? 12 : 1}
      InputProps={{ readOnly: true }}
      sx={{
        '& .MuiInputBase-input': { cursor: 'default' },
        '& .MuiOutlinedInput-root': {
          bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.grey[50]),
        },
      }}
    />
  );
};

const EditableAdditionalCommentsField: FC<{
  label: string;
  html: string;
  instanceKey: string;
  disabled?: boolean;
  onChange: (html: string) => void;
}> = ({ label, html, instanceKey, disabled, onChange }) => (
  <Box
    sx={{
      width: '100%',
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      overflow: 'hidden',
      bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.grey[50]),
      '& .ck.ck-editor__editable_inline': { minHeight: '140px !important' },
    }}>
    <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, pt: 1.25, pb: 0.5, fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ px: 1, pb: 1 }} key={instanceKey}>
      <CKEditor5
        label=""
        value={html}
        disabled={disabled}
        textChange={newContent => onChange(newContent)}
      />
    </Box>
  </Box>
);

const EditableBooleanField: FC<{
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}> = ({ label, checked, disabled, onChange }) => (
  <Box
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      px: 1.25,
      py: 0.75,
      minHeight: 40,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 0.75,
      bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.grey[50]),
    }}>
    <Switch
      checked={checked}
      disabled={disabled}
      size="small"
      sx={{ ml: -0.75 }}
      onChange={(_, v) => onChange(v)}
    />
    <Typography variant="body2" color="text.primary" component="span" sx={{ lineHeight: 1.35, flex: 1 }}>
      {label}
    </Typography>
  </Box>
);

export type SuperAdminBookingDetailsSectionProps = {
  booking: Record<string, unknown> | null;
  orderReference: string;
  sectionTitle: string;
  defaultExpanded?: boolean;
  htmlEditorInstanceKey: string;
  formatFieldValue: (raw: unknown, field: string) => string;
  onUpdated: (detail: SaBookingDetailPayload) => void;
};

export const SuperAdminBookingDetailsSection: FC<SuperAdminBookingDetailsSectionProps> = ({
  booking,
  orderReference,
  sectionTitle,
  defaultExpanded = false,
  htmlEditorInstanceKey,
  formatFieldValue,
  onUpdated,
}) => {
  const [form, setForm] = useState<BookingDetailsForm | null>(null);
  const [eventTypeOptions, setEventTypeOptions] = useState<DropdownOpt[]>([]);
  const [confirmationOptions, setConfirmationOptions] = useState<StringDropdownOpt[]>([]);
  const [salesAccountOptions, setSalesAccountOptions] = useState<DropdownOpt[]>([]);
  const [branchOptions, setBranchOptions] = useState<DropdownOpt[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get(URLS.getDataForNewBooking);
        if (!cancelled) {
          setEventTypeOptions(createDropdownData(data?.eventTypes, ['id', 'name']));
          setConfirmationOptions(createDropdownData(data?.bookingConfirmationByList, ['id', 'name']));
        }
      } catch {
        if (!cancelled) {
          setEventTypeOptions([]);
          setConfirmationOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get(URLS.getCompanyDropdownBooking);
        if (!cancelled) {
          setSalesAccountOptions(createDropdownData(data, ['id', 'name']));
        }
      } catch {
        if (!cancelled) setSalesAccountOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!booking) {
      setForm(null);
      return;
    }
    const holdRaw = booking.holdDays;
    const holdNum =
      typeof holdRaw === 'number' && Number.isFinite(holdRaw) ? Math.max(0, Math.floor(holdRaw)) : 0;
    setForm({
      startDate: rawToDateInput(booking.startDate),
      endDate: rawToDateInput(booking.endDate),
      eventTypeId: toId(booking.eventTypeId),
      bookingConfirmationBy: strEnum(booking.bookingConfirmationBy),
      bookingStatus: strEnum(booking.bookingStatus),
      holdDays: holdNum,
      referredBy: booking.referredBy == null ? '' : String(booking.referredBy),
      cancelReason: booking.cancelReason == null ? '' : String(booking.cancelReason),
      additionalComments:
        booking.additionalComments == null ? '' : String(booking.additionalComments),
      customerBooking: coerceBooleanDisplay(booking.customerBooking),
      isGuestBooking: coerceBooleanDisplay(booking.isGuestBooking),
      isCorporateBooking: coerceBooleanDisplay(booking.isCorporateBooking),
      isComplementary: coerceBooleanDisplay(booking.isComplementary),
      externalBooking: coerceBooleanDisplay(booking.externalBooking),
      salesAccountId: toId(booking.salesAccountId),
      salesBranchId: toId(booking.salesBranchId),
    });
  }, [booking]);

  const salesAccountIdForBranches = form?.salesAccountId ?? null;

  useEffect(() => {
    if (!salesAccountIdForBranches) {
      setBranchOptions([]);
      return;
    }
    let cancelled = false;
    void axiosInstance.get(URLS.getCompanyBranchesDropdown(salesAccountIdForBranches)).then(
      ({ data }) => {
        if (!cancelled) {
          setBranchOptions(createDropdownData(data, ['id', 'name']));
        }
      },
      () => {
        if (!cancelled) setBranchOptions([]);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [salesAccountIdForBranches]);

  const confirmationOptsMerged = useMemo(
    () => mergeStringOption(confirmationOptions, form?.bookingConfirmationBy ?? ''),
    [confirmationOptions, form?.bookingConfirmationBy],
  );

  const statusOptsMerged = useMemo(
    () => mergeStringOption(SA_BOOKING_DETAIL_STATUS_OPTIONS, form?.bookingStatus ?? ''),
    [form?.bookingStatus],
  );

  const handleSave = useCallback(async () => {
    const ref = orderReference.trim();
    if (!form || !ref) return;
    if (!form.startDate || !form.endDate) {
      setSaveError(strings.requiredText);
      return;
    }
    if (form.eventTypeId == null) {
      setSaveError(strings.sa_booking_lookup_event_type_required);
      return;
    }
    if (!form.bookingConfirmationBy || !form.bookingStatus) {
      setSaveError(strings.requiredText);
      return;
    }
    if (form.isCorporateBooking && (form.salesAccountId == null || form.salesBranchId == null)) {
      setSaveError(strings.sa_booking_lookup_corporate_sales_required);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const { data: res } = await axiosInstance.patch<SaBookingDetailPayload>(URLS.superAdminBookingBookingDetails(ref), {
        startDate: form.startDate,
        endDate: form.endDate,
        eventTypeId: form.eventTypeId,
        bookingConfirmationBy: form.bookingConfirmationBy,
        bookingStatus: form.bookingStatus,
        holdDays: form.holdDays,
        referredBy: form.referredBy.trim() || null,
        cancelReason: form.cancelReason.trim() || null,
        additionalComments: form.additionalComments || null,
        customerBooking: form.customerBooking,
        isGuestBooking: form.isGuestBooking,
        isCorporateBooking: form.isCorporateBooking,
        salesAccountId: form.isCorporateBooking ? form.salesAccountId : null,
        salesBranchId: form.isCorporateBooking ? form.salesBranchId : null,
        isComplementary: form.isComplementary,
        externalBooking: form.externalBooking,
      });
      onUpdated(res);
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { message?: string; error_description?: string } };
        message?: string;
      };
      const msg =
        err.response?.data?.message ?? err.response?.data?.error_description ?? err.message ?? 'Request failed';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  }, [form, orderReference, onUpdated]);

  const rowCount = BOOKING_DETAILS_SECTION_FIELDS.length;

  const sectionBody = useMemo(() => {
    if (!booking || !form) return null;
    return BOOKING_DETAILS_SECTION_FIELDS.map(field => {
      const label = getSaBookingFieldLabel(field);
      const wide = WIDE_FIELDS.has(field);
      const raw = booking[field];

      if (field === 'startDate' || field === 'endDate') {
        const key = field;
        const val = key === 'startDate' ? form.startDate : form.endDate;
        return (
          <Grid item xs={12} md={6} lg={4} key={field}>
            <TextField
              label={label}
              type="date"
              value={val}
              onChange={e =>
                setForm(f =>
                  f
                    ? {
                        ...f,
                        [key]: e.target.value,
                      }
                    : null,
                )
              }
              fullWidth
              variant="outlined"
              size="small"
              disabled={saving}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        );
      }

      if (field === 'eventTypeId') {
        return (
          <Grid item xs={12} md={6} lg={4} key={field}>
            <TextField
              select
              label={label}
              value={form.eventTypeId != null ? String(form.eventTypeId) : ''}
              onChange={e => {
                const v = e.target.value;
                setForm(f => (f ? { ...f, eventTypeId: v === '' ? null : Number(v) } : null));
              }}
              fullWidth
              variant="outlined"
              size="small"
              disabled={saving}
              SelectProps={{ displayEmpty: true }}>
              <MenuItem value="">{strings.sa_booking_lookup_geo_none}</MenuItem>
              {eventTypeOptions.map(opt => (
                <MenuItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        );
      }

      if (field === 'bookingConfirmationBy') {
        return (
          <Grid item xs={12} md={6} lg={4} key={field}>
            <TextField
              select
              label={label}
              value={form.bookingConfirmationBy}
              onChange={e => setForm(f => (f ? { ...f, bookingConfirmationBy: e.target.value } : null))}
              fullWidth
              variant="outlined"
              size="small"
              disabled={saving}
              SelectProps={{ displayEmpty: true }}>
              <MenuItem value="">{strings.sa_booking_lookup_geo_none}</MenuItem>
              {confirmationOptsMerged.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        );
      }

      if (field === 'bookingStatus') {
        return (
          <Grid item xs={12} md={6} lg={4} key={field}>
            <TextField
              select
              label={label}
              value={form.bookingStatus}
              onChange={e => {
                const v = e.target.value;
                setForm(f => {
                  if (!f) return null;
                  if (!statusAllowsCancelReasonEdit(v)) {
                    const cr = booking.cancelReason == null ? '' : String(booking.cancelReason);
                    return { ...f, bookingStatus: v, cancelReason: cr };
                  }
                  return { ...f, bookingStatus: v };
                });
              }}
              fullWidth
              variant="outlined"
              size="small"
              disabled={saving}
              SelectProps={{ displayEmpty: true }}>
              <MenuItem value="">{strings.sa_booking_lookup_geo_none}</MenuItem>
              {statusOptsMerged.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        );
      }

      if (field === 'holdDays') {
        return (
          <Grid item xs={12} md={6} lg={4} key={field}>
            <TextField
              label={label}
              type="number"
              onWheel={preventNumberInputScroll}
              value={String(form.holdDays)}
              onChange={e => {
                const n = parseInt(e.target.value, 10);
                setForm(f => (f ? { ...f, holdDays: Number.isFinite(n) && n >= 0 ? n : 0 } : null));
              }}
              fullWidth
              variant="outlined"
              size="small"
              disabled={saving}
              inputProps={{ min: 0 }}
            />
          </Grid>
        );
      }

      if (field === 'referredBy') {
        return (
          <Grid item xs={12} md={6} lg={4} key={field}>
            <TextField
              label={label}
              value={form.referredBy}
              onChange={e => setForm(f => (f ? { ...f, referredBy: e.target.value } : null))}
              fullWidth
              variant="outlined"
              size="small"
              disabled={saving}
            />
          </Grid>
        );
      }

      if (field === 'requestedChanges') {
        const value = formatFieldValue(raw, field);
        return (
          <Grid item xs={12} md={wide ? 12 : 6} lg={wide ? 12 : 4} key={field}>
            <ReadOnlyBookingField label={label} value={value} />
          </Grid>
        );
      }

      if (field === 'cancelReason') {
        const canEdit = statusAllowsCancelReasonEdit(form.bookingStatus);
        if (!canEdit) {
          const value = formatFieldValue(raw, field);
          return (
            <Grid item xs={12} md={wide ? 12 : 6} lg={wide ? 12 : 4} key={field}>
              <ReadOnlyBookingField label={label} value={value} />
            </Grid>
          );
        }
        return (
          <Grid item xs={12} md={wide ? 12 : 6} lg={wide ? 12 : 4} key={field}>
            <TextField
              label={label}
              value={form.cancelReason}
              onChange={e => setForm(f => (f ? { ...f, cancelReason: e.target.value } : null))}
              fullWidth
              variant="outlined"
              size="small"
              disabled={saving}
              multiline
              minRows={2}
              maxRows={12}
            />
          </Grid>
        );
      }

      if (field === 'additionalComments') {
        return (
          <Grid item xs={12} key={field}>
            <EditableAdditionalCommentsField
              label={label}
              html={form.additionalComments}
              instanceKey={`${htmlEditorInstanceKey}-${field}`}
              disabled={saving}
              onChange={html => setForm(f => (f ? { ...f, additionalComments: html } : null))}
            />
          </Grid>
        );
      }

      if (field === 'customerBooking') {
        return (
          <Grid item xs={12} md={6} lg={4} key={field}>
            <EditableBooleanField
              label={label}
              checked={form.customerBooking}
              disabled={saving}
              onChange={next => setForm(f => (f ? { ...f, customerBooking: next } : null))}
            />
          </Grid>
        );
      }

      if (field === 'isGuestBooking') {
        return (
          <Grid item xs={12} md={6} lg={4} key={field}>
            <EditableBooleanField
              label={label}
              checked={form.isGuestBooking}
              disabled={saving}
              onChange={next => setForm(f => (f ? { ...f, isGuestBooking: next } : null))}
            />
          </Grid>
        );
      }

      if (field === 'isComplementary') {
        return (
          <Grid item xs={12} md={6} lg={4} key={field}>
            <EditableBooleanField
              label={label}
              checked={form.isComplementary}
              disabled={saving}
              onChange={next => setForm(f => (f ? { ...f, isComplementary: next } : null))}
            />
          </Grid>
        );
      }

      if (field === 'externalBooking') {
        return (
          <Grid item xs={12} md={6} lg={4} key={field}>
            <EditableBooleanField
              label={label}
              checked={form.externalBooking}
              disabled={saving}
              onChange={next => setForm(f => (f ? { ...f, externalBooking: next } : null))}
            />
          </Grid>
        );
      }

      if (field === 'isCorporateBooking') {
        return (
          <React.Fragment key={field}>
            <Grid item xs={12} md={6} lg={4}>
              <EditableBooleanField
                label={label}
                checked={form.isCorporateBooking}
                disabled={saving}
                onChange={next =>
                  setForm(f =>
                    f
                      ? {
                          ...f,
                          isCorporateBooking: next,
                          ...(!next ? { salesAccountId: null, salesBranchId: null } : {}),
                        }
                      : null,
                  )
                }
              />
            </Grid>
            {form.isCorporateBooking && (
              <>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    select
                    label={strings.sa_booking_lookup_sales_account}
                    value={form.salesAccountId != null ? String(form.salesAccountId) : ''}
                    onChange={e => {
                      const v = e.target.value;
                      setForm(f =>
                        f
                          ? {
                              ...f,
                              salesAccountId: v === '' ? null : Number(v),
                              salesBranchId: null,
                            }
                          : null,
                      );
                    }}
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={saving}
                    SelectProps={{ displayEmpty: true }}>
                    <MenuItem value="">{strings.sa_booking_lookup_geo_none}</MenuItem>
                    {salesAccountOptions.map(opt => (
                      <MenuItem key={opt.value} value={String(opt.value)}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    select
                    label={strings.sa_booking_lookup_sales_branch}
                    value={form.salesBranchId != null ? String(form.salesBranchId) : ''}
                    onChange={e => {
                      const v = e.target.value;
                      setForm(f => (f ? { ...f, salesBranchId: v === '' ? null : Number(v) } : null));
                    }}
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={saving || form.salesAccountId == null}
                    SelectProps={{ displayEmpty: true }}>
                    <MenuItem value="">{strings.sa_booking_lookup_geo_none}</MenuItem>
                    {branchOptions.map(opt => (
                      <MenuItem key={opt.value} value={String(opt.value)}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}
          </React.Fragment>
        );
      }

      const value = formatFieldValue(raw, field);
      return (
        <Grid item xs={12} md={wide ? 12 : 6} lg={wide ? 12 : 4} key={field}>
          <ReadOnlyBookingField label={label} value={value} />
        </Grid>
      );
    });
  }, [
    booking,
    form,
    saving,
    eventTypeOptions,
    confirmationOptsMerged,
    statusOptsMerged,
    salesAccountOptions,
    branchOptions,
    formatFieldValue,
    htmlEditorInstanceKey,
  ]);

  if (!booking || !form) {
    return null;
  }

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        '&:before': { display: 'none' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1,
        overflow: 'hidden',
      }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle1" component="div" fontWeight={600}>
          {sectionTitle}
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            ({rowCount})
          </Typography>
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 2.5 }}>
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}
        <Grid container spacing={2}>
          {sectionBody}
        </Grid>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            pt: 2,
            mt: 1,
            borderTop: 1,
            borderColor: 'divider',
          }}>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => void handleSave()}
            disabled={saving}>
            {strings.sa_booking_lookup_save}
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
